import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

// ============================================================
// Sync Progress Hook — Polls backend for LexOffice sync status
// ============================================================

export interface SyncProgress {
	status: "running" | "done" | "error" | "aborted";
	phase: string;
	processed: number;
	total: number;
	error: string | null;
	updatedAt: string | null;
}

export function useSyncProgress() {
	const queryClient = useQueryClient();

	const query = useQuery({
		queryKey: ["lexoffice", "sync-progress"],
		queryFn: () =>
			api.get<{ data: SyncProgress | null }>("/lexoffice/sync-progress"),
		refetchInterval: (query) => {
			const progress = query.state.data?.data;
			return progress?.status === "running" ? 2000 : 30_000;
		},
	});

	const abortMutation = useMutation({
		mutationFn: () =>
			api.post<{ data: { ok: boolean } }>("/settings/sync/lexoffice/abort"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["lexoffice", "sync-progress"],
			});
		},
	});

	const progress = query.data?.data ?? null;

	// Staleness: Wenn Sync seit 60s nicht aktualisiert -> als tot behandeln
	const isStale =
		progress?.status === "running" &&
		progress.updatedAt &&
		Date.now() - new Date(progress.updatedAt).getTime() > 60_000;

	const isRunning = progress?.status === "running" && !isStale;
	const percent =
		isRunning && progress.total > 0
			? Math.round((progress.processed / progress.total) * 100)
			: 0;

	return { progress, isRunning, percent, abortMutation };
}
